import {
  EnumAvailability,
  POC_DISCOUNT_FILTER_VALUES,
} from '@/pages/Inventory/constants'

export const filtersDefault = {
  filters: {
    productName: undefined,
    isIdealPortfolio: undefined,
    brandId: undefined,
    offerState: undefined,
    isAvailable: undefined,
    isScheduledItemAvailable: undefined,
    categoriesId: undefined,
    productGroupId: undefined,
    isProductFavorite: undefined,
    unavailableDays: undefined,
    isExternalCodeSet: undefined,
    hasTakeRateSeller: undefined,
    categorizedProductGroupId: undefined,
    priceVariationType: undefined,
    hasActivePocDiscount: undefined,
    isBoostedProduct: undefined,
  },
  selectedIdealPortfolio: [],
  selectedAvailability: [],
  selectedOthers: [],
  activeFilters: [],
  wasFiltered: false,
}

const setSearch = (state, value) => ({
  ...state,
  filters: { ...state.filters, productName: value },
})

const setIsIdealPortfolio = (state, value) => {
  const idealPortfolio = Boolean(value[0])

  return {
    ...state,
    selectedIdealPortfolio: value,
    filters: { ...state.filters, isIdealPortfolio: idealPortfolio },
  }
}

const setBrandFilteredId = (state, value) => ({
  ...state,
  filters: { ...state.filters, brandId: value },
})

const setCategories = (state, value) => ({
  ...state,
  filters: { ...state.filters, categoriesId: value },
})

const setOfferState = (state, value) => ({
  ...state,
  filters: { ...state.filters, offerState: value },
})

const setProductGroupId = (state, value) => ({
  ...state,
  filters: { ...state.filters, productGroupId: value },
})

const setUnavailableDays = (state, value) => ({
  ...state,
  filters: { ...state.filters, unavailableDays: value },
})

const setFavorite = (state, value) => ({
  ...state,
  filters: { ...state.filters, isProductFavorite: value },
})

const setCategorizedProductGroupId = (state, value) => ({
  ...state,
  filters: { ...state.filters, categorizedProductGroupId: value },
})

const setPriceVariationType = (state, value) => ({
  ...state,
  filters: { ...state.filters, priceVariationType: value },
})

const checkAvailability = (value, availabilityType) => {
  const isActive = value.includes(availabilityType.ACTIVE.toString())
  const isInactive = value.includes(availabilityType.INACTIVE.toString())

  const hasOption = isActive || isInactive
  const hasBothOption = isActive && isInactive

  return hasOption && !hasBothOption ? isActive : undefined
}

const setScheduledAvailability = (state, value) => {
  const commonAvailability = checkAvailability(value, EnumAvailability.COMMON)
  const scheduledAvailability = checkAvailability(
    value,
    EnumAvailability.SCHEDULED
  )

  return {
    ...state,
    selectedAvailability: value,
    filters: {
      ...state.filters,
      isAvailable: commonAvailability,
      isScheduledItemAvailable: scheduledAvailability,
    },
  }
}

const setAvailability = (state, value) => {
  const availability = value.length === 2 ? undefined : value[0] === 'true'

  return {
    ...state,
    selectedAvailability: value,
    filters: {
      ...state.filters,
      isAvailable: availability,
    },
  }
}

const handleExternalCodeValue = value => {
  if (value.length) {
    return value.length === 2 ? undefined : value[0].includes('true')
  }

  return undefined
}

const getOtherValues = value => {
  const marketplace = value.includes('marketplace')
  const externalCode = value.filter(item => item.includes('external-code'))
  const hasActivePocDiscount = value.includes(POC_DISCOUNT_FILTER_VALUES.ACTIVE)
  const hasInactivePocDiscount = value.includes(
    POC_DISCOUNT_FILTER_VALUES.INACTIVE
  )

  let pocDiscountFilterValue = undefined
  if (hasActivePocDiscount && hasInactivePocDiscount) {
    pocDiscountFilterValue = undefined
  } else if (hasActivePocDiscount) {
    pocDiscountFilterValue = true
  } else if (hasInactivePocDiscount) {
    pocDiscountFilterValue = false
  }

  return {
    hasTakeRateSeller: marketplace ? marketplace : undefined,
    isExternalCodeSet: handleExternalCodeValue(externalCode),
    hasActivePocDiscount: pocDiscountFilterValue,
  }
}

const setOthers = (state, value) => {
  const newFilters = getOtherValues(value)
  const hadBothPocDiscountFilters =
    state.selectedOthers.includes(POC_DISCOUNT_FILTER_VALUES.ACTIVE) &&
    state.selectedOthers.includes(POC_DISCOUNT_FILTER_VALUES.INACTIVE)

  const hasBothPocDiscountFilters =
    value.includes(POC_DISCOUNT_FILTER_VALUES.ACTIVE) &&
    value.includes(POC_DISCOUNT_FILTER_VALUES.INACTIVE)

  if (hasBothPocDiscountFilters) {
    newFilters.isBoostedProduct = true
  } else if (hadBothPocDiscountFilters && !hasBothPocDiscountFilters) {
    newFilters.isBoostedProduct = undefined
  }

  return {
    ...state,
    selectedOthers: value,
    filters: {
      ...state.filters,
      ...newFilters,
    },
  }
}

const setIsBoostedProduct = (state, value) => ({
  ...state,
  filters: { ...state.filters, isBoostedProduct: value },
})

const filterIsAlreadyActive = ({ activeFilters }, filterType) =>
  activeFilters.includes(filterType)

const addActiveFilter = (state, filterType) => {
  const activeFilters = [...state.activeFilters]
  if (!filterIsAlreadyActive(state, filterType)) {
    activeFilters.push(filterType)
  }
  return { ...state, activeFilters }
}

const removeInactiveFilter = (state, filterType) => {
  const activeFilters = [...state.activeFilters]
  const index = activeFilters.indexOf(filterType)
  if (index >= 0) {
    activeFilters.splice(index, 1)
  }
  return { ...state, activeFilters }
}

const clearFilters = (_, persistedState) => {
  return {
    ...filtersDefault,
    ...persistedState,
  }
}

const setWasFiltered = (state, value) => ({ ...state, wasFiltered: value })

const filterActions = {
  SEARCH: setSearch,
  SCHEDULED_AVAILABILITY: setScheduledAvailability,
  AVAILABILITY: setAvailability,
  ACTIVE_FILTER: addActiveFilter,
  INACTIVE_FILTER: removeInactiveFilter,
  CLEAR_FILTERS: clearFilters,
  BRAND_FILTER: setBrandFilteredId,
  IDEAL_PORTFOLIO: setIsIdealPortfolio,
  WAS_FILTERED: setWasFiltered,
  CATEGORIES: setCategories,
  OFFER_STATE: setOfferState,
  GROUP_ID: setProductGroupId,
  UNAVAILABLE_DAYS: setUnavailableDays,
  FAVORITE: setFavorite,
  OTHERS_FILTER: setOthers,
  CATEGORIZED_PRODUCT_GROUP_ID: setCategorizedProductGroupId,
  IDEALPORTFOLIO: setCategorizedProductGroupId,
  BEST_SELLERS_REGION: setCategorizedProductGroupId,
  WINES_SELLERS: setCategorizedProductGroupId,
  DISTILLED_SELLERS: setCategorizedProductGroupId,
  SNACK_SELLERS: setCategorizedProductGroupId,
  GROCERY_SELLERS: setCategorizedProductGroupId,
  SPECIAL_OFFER: setCategorizedProductGroupId,
  SELLER_PROMO_SPECIAL_OFFER: setCategorizedProductGroupId,
  MOST_WANTED_SELLERS: setCategorizedProductGroupId,
  NEW_PRODUCTS: setCategorizedProductGroupId,
  PRICE_VARIATION: setPriceVariationType,
  IS_BOOSTED_PRODUCT: setIsBoostedProduct,
  MORE_COMPETITIVE_REGION: setCategorizedProductGroupId,
  MOST_SEARCHED_PRODUCTS: setCategorizedProductGroupId,
  COMPLEMENT_CUP_ORDERS: setCategorizedProductGroupId,
}

const filtersReducer = (state, action) => {
  return filterActions[action.type](state, action.value)
}

export default filtersReducer
